import { reactive } from "vue";
import MaklerDatenService from "@/businesslogic/services/MaklerDatenService";
import dataUtils from "@/businesslogic/utils/dataUtils";
import formStoreInitializer from "@/businesslogic/stores/formStoreInitializer";
import AnfrageService from "@/businesslogic/services/AnfrageService";
import Logger from "js-logger";
import dateUtils from "@/businesslogic/utils/dateUtils";
import { Anfrage } from "@/model/Anfrage";
import { ProduktLinie } from "@/model/ProduktLinie";

import { defineStore } from "pinia";
import cookieUtils from "@/businesslogic/utils/cookieUtils";

export const useFormStore = defineStore(
  "form-store",
  () => {
    const { copyInto } = dataUtils();
    const { dateStringToIsoDate, getNow } = dateUtils();
    const { moeglichesPolicenStartdatum } = formStoreInitializer();

    function resetState() {
      Object.assign(state, new Anfrage());
    }

    const state: Anfrage = reactive(new Anfrage());

    function sollVermittlernummerSetzen(data: any) {
      return data.isMakler || data.isMaklerLink;
    }

    function setStateData(data: any): boolean {
      //da state reaktiv ist, kann nicht das ganze Objekt ersetzt werden.
      try {
        if (data) {
          state.id = data.id;
          state.isMakler = data.isMakler;
          state.isMaklerLink = data.isMaklerLink;
          state.anfrageNummer = data.anfrageNummer;
          if (sollVermittlernummerSetzen(data)) {
            state.vermittlerNummer = data.vermittlerNummer;
          }
          state.status = data.status;
          state.erstelldatum = data.erstelldatum;
          state.isDunkelverarbeitung = data.isDunkelverarbeitung;
          state.korrespondenz = data.korrespondenz;
          state.personendaten = data.personendaten;
          state.tarifinformationen = data.tarifinformationen;
          state.gebaeudedaten = data.gebaeudedaten;
          state.zahlungsdaten = data.zahlungsdaten;
          state.verkaufsprodukt = data.verkaufsprodukt;
          state.vorversicherung = data.vorversicherung;
          if (data.vorschaeden && data.vorschaeden.length > 0) {
            state.vorschaeden = data.vorschaeden;
          }
          state.risikoAnschrift = data.risikoAnschrift;
          state.preise = data.preise;
          state.modulPreise = data.modulPreise;
          state.adresseFelderSchrittDreiDeaktivieren = data.adresseFelderSchrittDreiDeaktivieren;
          state.zuersZone = data.zuersZone;
          state.ferienhaus = data.ferienhaus;
          state.gebaeudeRisikoIndex = data.gebaeudeRisikoIndex;
          state.tarifOptionenZuVerbergen = data.tarifOptionenZuVerbergen;
          state.antragssteller = data.antragssteller;
          state.bauleitungDurchDritte = data.bauleitungDurchDritte;
          state.abTest = data.abTest;
          state.dateien = data.dateien;
          state.affiliate = data.affiliate;
          state.korrespondenz.beilagen = data.isMakler ? "VORSCHLAG_UND_PRODUKTINFORMATIONEN" : "VOLLSTAENDIG";
          state.outbound = data.outbound;

          versicherungsbeginnPruefen(state.tarifinformationen.versicherungsbeginn);

          return true;
        }
      } catch (ex) {
        Logger.debug("++++++ Fehler in setStateData ++++++");
        Logger.debug("Fehler: ", ex);
      }
      return false;
    }

    function aktualisiereAussteuerungen(data: any) {
      Logger.debug("formStore > aktualisiereAussteuerungen", data);

      state.nurSanierungAusgesteuert = data.nurSanierungAusgesteuert;
      state.sanierungAusgesteuert = data.sanierungAusgesteuert;
      state.gewerbeArtAusgesteuert = data.gewerbeArtAusgesteuert;
      state.anschriftAusgesteuert = data.anschriftAusgesteuert;
      state.baujahrAusgesteuert = data.baujahrAusgesteuert;
      state.leerstandAusgesteuert = data.leerstandAusgesteuert;
      state.denkmalschutzAusgesteuert = data.denkmalschutzAusgesteuert;
    }

    const { storeInitialisiereStandard, ladeVermittlerdaten, ladeAbsenderdaten } = formStoreInitializer();

    function uebernehmeDatenAusVorbelegung(vorbelegungsId: string) {
      MaklerDatenService.ladeVorbelegung(vorbelegungsId).then((res) => {
        if (res.status == 200) {
          res.data.id = undefined;
          copyInto(res.data, state);
          ladeVermittlerdaten(state);
          ladeAbsenderdaten(state);
        }
      });
      state.vorbelegungsId = vorbelegungsId;
    }

    function storeInitialisieren(
      isMakler: boolean,
      isMaklerLink: boolean,
      vorbelegungsId: string | undefined,
      vermittlerNummer: string | undefined,
      produkt: string,
    ) {
      Logger.debug(
        "storeInitialisieren(). Initialisiere Speicher.",
        "Produkt: " + produkt,
        "VorbelegungsId: " + vorbelegungsId,
        "Vermittlernummer: " + vermittlerNummer,
      );
      if (vorbelegungsId) {
        Logger.debug(
          "storeInitialisieren(): Lade Standardwerte und übernehme Daten aus VorbelegungsId " + vorbelegungsId,
        );
        resetState();
        storeInitialisiereStandard(state, produkt.toLowerCase(), isMakler, isMaklerLink, vorbelegungsId);
        uebernehmeDatenAusVorbelegung(vorbelegungsId);
      } else if (cookieUtils().getBase64Cookie("tarifrechner-init")) {
        Logger.debug("storeInitialisieren(): Lade Standardwerte durch Cookie.");
        resetState();
        storeInitialisiereStandard(state, produkt.toLowerCase(), isMakler, isMaklerLink, vorbelegungsId);
      } else if (
        isMakler === state.isMakler &&
        isMaklerLink === state.isMaklerLink &&
        state.verkaufsprodukt &&
        state.verkaufsprodukt.toLowerCase() === produkt.toLowerCase()
      ) {
        Logger.debug("Kein Produktwechsel innerhalb einer Sitzung.");
      } else {
        resetState();
        Logger.debug("storeInitialisieren(): Lade Standardwerte.");
        storeInitialisiereStandard(state, produkt.toLowerCase(), isMakler, isMaklerLink, vorbelegungsId);
      }

      if (vermittlerNummer) {
        state.vermittlerNummer = vermittlerNummer;
      }
    }

    function tariff60PlusAktualisieren(anfrage: Anfrage, isMakler: boolean) {
      Logger.debug("formStore > tariff60PlusAktualisieren: Eingehende Anfrage mit tariffVariante: ", anfrage);
      if (state.verkaufsprodukt === "VHV2024" || state.verkaufsprodukt === "FH2024" || isMakler) {
        state.tarifinformationen.tariff60Plus = anfrage.tarifinformationen.tariff60Plus;
      }
    }

    const setEnergieEffizienzDaten = (anfrageMitPreisen: any) => {
      const gebaeudedaten = anfrageMitPreisen.gebaeudedaten;
      state.gebaeudedaten.denkmalschutz = gebaeudedaten.denkmalschutz;
      state.gebaeudedaten.eek = gebaeudedaten.eek;
      state.gebaeudedaten.eekHash = gebaeudedaten.eekHash;
    };

    function preiseAktualisieren(anfrageMitPreisen: any) {
      Logger.debug("formStore > preiseAktualisieren(). Eingehende Anfrage mit Preisen: ", anfrageMitPreisen);

      setEnergieEffizienzDaten(anfrageMitPreisen);

      // TODO warum übernehmen wir eigentlich nicht alle Werte aus der Antwort des Backends?
      state.modulPreise = anfrageMitPreisen.modulPreise;

      function setEinmalPreise() {
        state.preise.produktLinien = [];
        const produktlinie = new ProduktLinie();
        produktlinie.einmalig = anfrageMitPreisen.preise.produktLinien[0].einmalig;
        state.preise.produktLinien.push(produktlinie);
      }

      function erzeugeKorrektenLiniennamen(linienname: string) {
        const ersterBuchstabe = linienname.slice(0, 1).toUpperCase();
        const rest = linienname.slice(1, linienname.length).toLowerCase();
        return ersterBuchstabe.concat(rest);
      }

      function setMehrfachPreise() {
        state.preise.produktLinien = [];
        const preise = anfrageMitPreisen.preise;
        const anzahlTariflinien = preise.produktLinien.length;
        for (let linie = 0; linie < anzahlTariflinien; linie++) {
          const produktlinie = new ProduktLinie();
          produktlinie.jaehrlich = preise.produktLinien[linie].jaehrlich;
          produktlinie.halbjaehrlich = preise.produktLinien[linie].halbjaehrlich;
          produktlinie.vierteljaehrlich = preise.produktLinien[linie].vierteljaehrlich;
          produktlinie.monatlich = preise.produktLinien[linie].monatlich;
          produktlinie.nameProduktLinie = erzeugeKorrektenLiniennamen(preise.produktLinien[linie].nameProduktLinie);
          state.preise.produktLinien.push(produktlinie);
        }
      }

      switch (state.verkaufsprodukt) {
        case "BL":
        case "BH":
          setEinmalPreise();
          break;
        default:
          setMehrfachPreise();
          break;
      }

      //Preis für gewählte Zahlungsweise setzen für einfachen Zugriff innerhalb der App
      for (const produktLinie of state.preise.produktLinien) {
        if (state.tarifinformationen.zahlungsweise == 12) {
          produktLinie.gemZahlungsweise = produktLinie.monatlich;
        } else if (state.tarifinformationen.zahlungsweise == 4) {
          produktLinie.gemZahlungsweise = produktLinie.vierteljaehrlich;
        } else if (state.tarifinformationen.zahlungsweise == 2) {
          produktLinie.gemZahlungsweise = produktLinie.halbjaehrlich;
        } else {
          produktLinie.gemZahlungsweise = produktLinie.jaehrlich;
        }
      }

      //produktLinienIndex setzen
      if (state.verkaufsprodukt === "HG") {
        if (state.tarifinformationen.produktLinie === "002") {
          state.preise.produktLinienIndex = 0;
        } else if (state.tarifinformationen.produktLinie === "003") {
          state.preise.produktLinienIndex = 1;
        }
      } else {
        if (state.tarifinformationen.produktLinie === "001") {
          state.preise.produktLinienIndex = 0;
        } else if (state.tarifinformationen.produktLinie === "002") {
          state.preise.produktLinienIndex = 1;
        } else if (state.tarifinformationen.produktLinie === "003") {
          state.preise.produktLinienIndex = 2;
        }
      }

      Logger.debug("state.preise", state.preise);
    }

    function zuersUndGriLoeschen() {
      griLoeschen();
      zuersLoeschen();
    }

    function zuersLoeschen() {
      state.zuersZone = {
        zone: null,
        bach: "",
        kueste: "",
        starkregen: "",
        erdbebenzone: false,
      };
    }

    function griLoeschen() {
      state.gebaeudeRisikoIndex = "";
    }

    function zuersUndGriAktualisieren(data: any) {
      state.gebaeudeRisikoIndex = data.gebaeudeRisikoIndex;
      state.zuersZone = data.zuersZone;
    }

    function versicherungsbeginnPruefen(beginnDatum: any) {
      const versicherungsbeginn = dateStringToIsoDate(beginnDatum);
      if (versicherungsbeginn < getNow()) {
        Logger.debug("Versicherungsbeginn lag in der Vergangenheit und wurde aktualisiert");
        state.tarifinformationen.versicherungsbeginn = moeglichesPolicenStartdatum();
      }
    }

    function vorschlagWiederherstellen(anfrageId: string | undefined, isMakler: boolean, produkt: string) {
      if (anfrageId != undefined && isMakler) {
        Logger.debug("Ein existierender Vorschlag wird vom Backend abgeholt", anfrageId);
        const store = new Anfrage();
        store.loading = true;

        AnfrageService.laden(
          getSessionToken(undefined),
          true,
          produkt,
          anfrageId,
          "Maklerportal",
          "Basisdaten" + produkt.toUpperCase(),
        )
          .then((res) => {
            // a non-200 response code
            Logger.debug("res: ", res);
            if (res.status != 200) {
              // create error instance with HTTP status text
              store.loading = false;
              throw new Error(res.statusText);
            }
            return res.data;
          })
          .then((data) => {
            Logger.debug("Response Data in den Store setzen...", data);
            setStateData(data);

            if (state.sessionId === undefined || state.sessionId === "") {
              state.sessionId = store.sessionId;
            }

            ladeVermittlerdaten(state);
            ladeAbsenderdaten(state);
            store.loading = false;
          });
      }
    }

    const quelleInitialisieren = (produkt: string, quelle: string) => {
      Logger.debug("quelleInitialisieren", produkt, quelle);

      switch (produkt) {
        case "vgv":
          if (quelle === "bg") {
            state.tarifinformationen.proklima = true;
          }
          break;
        default:
      }
    };

    const getSessionToken = (state: any) => {
      return state && state.sessionId ? state.sessionId : "";
    };

    return {
      state,
      storeInitialisieren,
      storeInitialisiereStandard,
      setStateData,
      preiseAktualisieren,
      tariff60PlusAktualisieren,
      zuersUndGriLoeschen,
      zuersUndGriAktualisieren,
      vorschlagWiederherstellen,
      resetState,
      aktualisiereAussteuerungen,
      griLoeschen,
      quelleInitialisieren,
      getSessionToken,
    };
  },
  {
    persist: {
      enabled: true,
    },
  },
);
